import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import Swal from "sweetalert2";
import Joi from "joi-browser";
import useForm from "../../app/hooks/useForm";
import useControls from "../../app/hooks/useControls";
import { useDispatch, useSelector } from "react-redux";
import { addUserSecurityQuestion, getSecQuestions, getSecQuestionUpdatedStatus, loadSecQuestions, secQuestionsUpdated } from "../../app/store/users";
import { getAlert } from "../../Services/appAlerts";
import { validatePassword } from "../../Services/passwordValidator";

const SECURITY_QUESTION_STYLES = {
  modalContent: {
    borderRadius: "0px",
    overflow: "hidden",
    border: "1px solid rgba(226, 232, 240, 1)",
    boxShadow: "0 24px 60px rgba(15, 23, 42, 0.16)"
  },
  modalDialog: {
    maxWidth: "780px"
  },
  modalHeader: {
    padding: "18px 22px",
    borderBottom: "1px solid #e2e8f0",
    background: "#ffffff"
  },
  headerContent: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "14px",
    paddingRight: "0"
  },
  titleWrap: {
    display: "flex",
    alignItems: "center",
    gap: "14px"
  },
  headerIcon: {
    width: "44px",
    height: "44px",
    borderRadius: "0px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f1f5f9",
    color: "#0f172a",
    fontSize: "20px",
    boxShadow: "none"
  },
  eyebrow: {
    fontSize: "11px",
    fontWeight: 600,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "#4f46e5",
    marginBottom: "4px"
  },
  title: {
    fontSize: "21px",
    fontWeight: 600,
    color: "#0f172a",
    lineHeight: 1.1,
    margin: 0
  },
  subtitle: {
    fontSize: "12px",
    color: "#64748b",
    marginTop: "4px"
  },
  closeButton: {
    width: "38px",
    height: "38px",
    borderRadius: "0px",
    border: "1px solid rgba(226, 232, 240, 1)",
    background: "#ffffff",
    color: "#64748b",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "16px",
    fontWeight: 600,
    boxShadow: "0 2px 6px rgba(15, 23, 42, 0.04)",
    cursor: "pointer",
    transition: "all 0.2s ease",
    flexShrink: 0
  },
  modalBody: {
    padding: "20px 22px",
    background: "#ffffff",
    minHeight: "320px",
    maxHeight: "calc(100vh - 240px)",
    overflowY: "auto",
    overflowX: "hidden"
  },
  helperCard: {
    borderRadius: "0px",
    padding: "16px",
    background: "#ffffff",
    border: "1px solid rgba(191, 219, 254, 0.9)",
    marginBottom: "16px"
  },
  helperTitle: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#0f172a",
    marginBottom: "8px"
  },
  helperText: {
    fontSize: "12px",
    lineHeight: 1.6,
    color: "#64748b",
    marginBottom: "10px"
  },
  helperList: {
    margin: 0,
    paddingLeft: "18px",
    color: "#475569",
    fontSize: "12px",
    lineHeight: 1.7
  },
  formCard: {
    borderRadius: "0px",
    border: "1px solid rgba(226, 232, 240, 1)",
    background: "#ffffff",
    padding: "18px 18px 6px 18px"
  },
  sectionTitle: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#0f172a",
    marginBottom: "12px"
  },
  sectionSubtitle: {
    fontSize: "12px",
    color: "#64748b",
    marginBottom: "14px",
    lineHeight: 1.6
  },
  modalFooter: {
    padding: "16px 22px 20px 22px",
    borderTop: "1px solid rgba(226, 232, 240, 0.9)",
    background: "#ffffff"
  },
  footerActions: {
    width: "100%",
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px"
  },
  cancelButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    borderRadius: "0px",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    padding: "9px 18px",
    fontSize: "13px",
    fontWeight: 600,
    boxShadow: "0 2px 6px rgba(15, 23, 42, 0.04)",
    cursor: "pointer",
    transition: "all 0.2s ease"
  },
  saveButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    borderRadius: "0px",
    border: "1px solid #2563eb",
    background: "#2563eb",
    color: "#ffffff",
    padding: "9px 18px",
    fontSize: "13px",
    fontWeight: 600,
    boxShadow: "0 2px 6px rgba(37, 99, 235, 0.2)",
    cursor: "pointer",
    transition: "all 0.2s ease"
  },
  saveButtonDisabled: {
    opacity: 0.55,
    boxShadow: "none"
  }
};

const AddSecurityQuestion = (props) => {
  const { securityShowHide, handleSecurityShowHide, userDetails, firstLogin } = props;

  const [secQuestion1, setSecQuestion1] = useState([]);
  const [secQuestion2, setSecQuestion2] = useState([]);
  const [secQuestion3, setSecQuestion3] = useState([]);
  const [btnSaveProcess, setBtnSaveProcess] = useState(false);

  const secQuestionsSelector = useSelector(getSecQuestions);
  const secQuestionUpdatedStatusSelector = useSelector(getSecQuestionUpdatedStatus);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(loadSecQuestions("Security Question 1"));
  }, []);

  useEffect(() => {
    setSecQuestion1(secQuestionsSelector);
    setSecQuestion2(secQuestionsSelector);
    setSecQuestion3(secQuestionsSelector);
  }, [secQuestionsSelector]);

  const schema = {
    oldPassword: Joi.string().required().label("Login Password"),
    ...(firstLogin ? {
      newPassword: Joi.string().required().min(8).max(14).label("New Password"),
      confirmPassword: Joi.string()
        .valid(Joi.ref("newPassword"))
        .required()
        .options({ language: { any: { allowOnly: "must match New Password" } } })
        .label("Confirm Password"),
    } : {}),
    hintQuestion1: Joi.string().required().label("Security Question 1"),
    hintQuestion2: Joi.string().required().label("Security Question 2"),
    hintQuestion3: Joi.string().required().label("Security Question 3"),
    hintAnswer1: Joi.string().required().label("Answer 1"),
    hintAnswer2: Joi.string().required().label("Answer 2"),
    hintAnswer3: Joi.string().required().label("Answer 3"),
  };

  const doSubmit = () => {
    if (firstLogin) {
      const passResult = validatePassword(stateData["newPassword"]);
      if (!passResult.isValid) {
        getAlert(
          "Strong Password Required",
          passResult.htmlMessage || passResult.message,
          "error"
        );
        return;
      }
    }
    setBtnSaveProcess(true);
    const newData = { ...stateData };
    newData["id"] = userDetails.performerid;
    dispatch(addUserSecurityQuestion(newData));
  };

  useEffect(() => {
    if (secQuestionUpdatedStatusSelector) {
      if (firstLogin) {
        Swal.fire({
          title: "",
          text: "You are logged out. Please log in with your new password.",
          icon: "success",
          confirmButtonText: "Close",
          customClass: {
            confirmButton: "swal2-outline-red",
          },
          didOpen: () => {
            const confirmButton = Swal.getConfirmButton();
            confirmButton.innerHTML = `
              <i class="fa fa-times" style="margin-right: 8px; margin-left:3px;"></i> Close`;
          },
        }).then((result) => {
          if (result.isConfirmed) {
            window.location.href = "/logout";
          }
        });
      } else {
        getAlert("", "Saved Successfully", "success");
      }
      // handleSecurityShowHide();
    }
    else if (secQuestionUpdatedStatusSelector === false) {
      getAlert("", "Login Password is incorrect", "error");
    }
    dispatch(secQuestionsUpdated(null));
    setBtnSaveProcess(false);
  }, [secQuestionUpdatedStatusSelector]);

  const { handleChange, stateData, stateErrors, handleSubmit, validate } = useForm(schema, doSubmit);

  const { renderSelect, renderInput } = useControls(stateData, stateErrors, handleChange, schema);

  let question1 = [...secQuestion1];
  let question2 = [...secQuestion2];
  let question3 = [...secQuestion3];
  if (stateData["hintQuestion1"]) {
    question2 = question2.filter(
      (x) => x.id !== parseInt(stateData["hintQuestion1"])
    );
    question3 = question3.filter(
      (x) => x.id !== parseInt(stateData["hintQuestion1"])
    );
  }
  if (stateData["hintQuestion2"]) {
    question1 = question1.filter(
      (x) => x.id !== parseInt(stateData["hintQuestion2"])
    );
    question3 = question3.filter(
      (x) => x.id !== parseInt(stateData["hintQuestion2"])
    );
  }
  if (stateData["hintQuestion3"]) {
    question1 = question1.filter(
      (x) => x.id !== parseInt(stateData["hintQuestion3"])
    );
    question2 = question2.filter(
      (x) => x.id !== parseInt(stateData["hintQuestion3"])
    );
  }

  return (
    <Modal
      show={securityShowHide}
      size="lg"
      centered
      onHide={!firstLogin ? handleSecurityShowHide : undefined}
      backdrop={firstLogin ? "static" : true}
      keyboard={!firstLogin}
      dialogClassName="security-question-modal-dialog"
      contentClassName="security-question-modal-content"
    >
      <style>
        {`
          .security-question-modal-content,
          .modal-content {
            border-radius: 0px !important;
            overflow: ${SECURITY_QUESTION_STYLES.modalContent.overflow};
            border: ${SECURITY_QUESTION_STYLES.modalContent.border};
            box-shadow: ${SECURITY_QUESTION_STYLES.modalContent.boxShadow};
          }
          .security-question-modal-content .form-control {
            border-radius: 0px !important;
          }
        `}
      </style>
      <Modal.Header style={SECURITY_QUESTION_STYLES.modalHeader}>
        <div style={SECURITY_QUESTION_STYLES.headerContent}>
          <Modal.Title as="div" style={SECURITY_QUESTION_STYLES.titleWrap}>
            <div style={SECURITY_QUESTION_STYLES.headerIcon}>
              <i className="fa fa-shield" aria-hidden="true"></i>
            </div>
            <div>
              <div style={SECURITY_QUESTION_STYLES.title}>Change Security Questions</div>
              <div style={SECURITY_QUESTION_STYLES.subtitle}>Set recovery questions to help protect and recover your account.</div>
            </div>
          </Modal.Title>
          {!firstLogin && (
            <button
              type="button"
              style={SECURITY_QUESTION_STYLES.closeButton}
              onClick={handleSecurityShowHide}
              aria-label="Close security questions"
            >
              <i className="fa fa-times" aria-hidden="true"></i>
            </button>
          )}
        </div>
      </Modal.Header>
      <Modal.Body style={SECURITY_QUESTION_STYLES.modalBody}>
        <div style={SECURITY_QUESTION_STYLES.helperCard}>
          <div style={SECURITY_QUESTION_STYLES.helperTitle}>Recovery guidance</div>
          <div style={SECURITY_QUESTION_STYLES.helperText}>
            Choose three different security questions and provide answers you can remember easily. These will be used to help recover your User ID, username, and password.
          </div>
          <ul style={SECURITY_QUESTION_STYLES.helperList}>
            <li>Use answers that are memorable to you but hard for others to guess.</li>
            <li>Avoid repeating the same question or answer across all three entries.</li>
            <li>Keep your existing password ready so we can verify this update securely.</li>
          </ul>
        </div>
        {firstLogin && (
          <div style={SECURITY_QUESTION_STYLES.helperCard}>
            <div style={SECURITY_QUESTION_STYLES.helperTitle}>Password requirements</div>
            <div style={SECURITY_QUESTION_STYLES.helperText}>
              Use a password that is hard to guess and easy for you to remember.
            </div>
            <ul style={SECURITY_QUESTION_STYLES.helperList}>
              <li>Must be 8 to 14 characters and contain no spaces.</li>
              <li>Must include uppercase letters (A-Z) and lowercase letters (a-z).</li>
              <li>Must include at least one number (0-9).</li>
              <li>Must include at least one special character such as `!`, `@`, `#`, `$`, `%`, or `?`.</li>
            </ul>
          </div>
        )}
        <div style={SECURITY_QUESTION_STYLES.formCard}>
          <div style={SECURITY_QUESTION_STYLES.sectionTitle}>Verify your identity</div>
          <div style={SECURITY_QUESTION_STYLES.sectionSubtitle}>
            Confirm your current password before updating your recovery setup.
          </div>
          <div className="row">
            <div className="col-md-12">
              {renderInput("oldPassword", "Existing Password", "password")}
            </div>
            {firstLogin &&
              <>
                <div className="col-md-6">
                  {renderInput("newPassword", "New Password", "password")}
                </div>

                <div className="col-md-6">
                  {renderInput(
                    "confirmPassword",
                    "Confirm Password",
                    "password"
                  )}
                </div>
              </>
            }
          </div>
          <div style={{ ...SECURITY_QUESTION_STYLES.sectionTitle, marginTop: "8px" }}>Security questions</div>
          <div style={SECURITY_QUESTION_STYLES.sectionSubtitle}>
            Pick three unique questions and provide the answers exactly as you want them stored.
          </div>
          <div className="row">
            <div className="col-md-6">
              {renderSelect(
                "hintQuestion1",
                "Security Question 1",
                question1,
                "name",
                "id"
              )}
            </div>
            <div className="col-md-6">
              {renderInput("hintAnswer1", "Enter Answer 1", "password")}
            </div>
            <div className="col-md-6">
              {renderSelect(
                "hintQuestion2",
                "Security Question 2",
                question2,
                "name",
                "id"
              )}
            </div>
            <div className="col-md-6">
              {renderInput("hintAnswer2", "Enter Answer 2", "password")}
            </div>
            <div className="col-md-6">
              {renderSelect(
                "hintQuestion3",
                "Security Question 3",
                question3,
                "name",
                "id"
              )}
            </div>
            <div className="col-md-6">
              {renderInput("hintAnswer3", "Enter Answer 3", "password")}
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer style={SECURITY_QUESTION_STYLES.modalFooter}>
        <div style={SECURITY_QUESTION_STYLES.footerActions}>
          {!firstLogin && (
            <button
              type="button"
              style={SECURITY_QUESTION_STYLES.cancelButton}
              onClick={handleSecurityShowHide}
            >
              <i className="fa fa-times" aria-hidden="true"></i>
              Cancel
            </button>
          )}
          <button
            type="button"
            style={{
              ...SECURITY_QUESTION_STYLES.saveButton,
              ...((btnSaveProcess || validate()) ? SECURITY_QUESTION_STYLES.saveButtonDisabled : {})
            }}
            disabled={btnSaveProcess || validate()}
            onClick={handleSubmit}
          >
            {btnSaveProcess ? <i className="fa fa-spinner fa-spin mr-1"></i> : <i className="fa fa-check mr-1" aria-hidden="true"></i>}
            Save Security Questions
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export default AddSecurityQuestion;
